import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { MagnifyingGlassIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { getRecentMessages } from '../utils/dashboardData';
import type { Message } from '../types/Dashboard';

const MessagesPage: React.FC = () => {
  const [selectedChat, setSelectedChat] = useState<Message | null>(null);
  const [messageText, setMessageText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const messages = getRecentMessages();

  const filteredMessages = messages.filter(message =>
    message.sender.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    message.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSendMessage = () => {
    if (!messageText.trim()) return;
    // Here you would typically send the message via an API
    setMessageText('');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="p-8 max-w-7xl mx-auto"
    >
      <div className="flex h-[calc(100vh-8rem)] bg-white rounded-xl shadow-sm overflow-hidden">
        {/* Messages Sidebar */}
        <div className="w-96 border-r border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h1 className="text-xl font-semibold text-gray-900">Messages</h1>
            <p className="text-sm text-gray-500 mt-1">Communicate with your guests</p>
            <div className="relative mt-4">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search messages..."
                className="pl-10 pr-4 py-2 w-full border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-airbnb-red focus:border-transparent"
              />
            </div>
          </div>

          <div className="overflow-y-auto h-[calc(100%-5rem)]">
            {filteredMessages.map((message) => (
              <div
                key={message.id}
                onClick={() => setSelectedChat(message)}
                className={`
                  p-4 cursor-pointer transition-colors duration-200
                  ${selectedChat?.id === message.id ? 'bg-red-50' : 'hover:bg-gray-50'}
                `}
              >
                <div className="flex items-start space-x-4">
                  <div className="relative">
                    <img
                      src={message.sender.image}
                      alt={message.sender.name}
                      className="h-12 w-12 rounded-full object-cover"
                    />
                    <span className={`
                      absolute -bottom-1 -right-1 h-4 w-4 border-2 border-white rounded-full
                      ${message.sender.status === 'online' ? 'bg-green-500' : 'bg-gray-300'}
                    `} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-gray-900">{message.sender.name}</h3>
                      <span className="text-xs text-gray-500">
                        {format(message.timestamp, 'MMM d, h:mm a')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 truncate">{message.preview}</p>
                    {message.unread && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 mt-1">
                        New
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Chat Area */}
        {selectedChat ? (
          <div className="flex-1 flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <img
                    src={selectedChat.sender.image}
                    alt={selectedChat.sender.name}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                  <div>
                    <h2 className="text-lg font-medium text-gray-900">{selectedChat.sender.name}</h2>
                    <p className="text-sm text-gray-500">
                      {selectedChat.sender.status === 'online' ? 'Online' : 'Offline'}
                    </p>
                  </div>
                </div>
                <button className="text-gray-400 hover:text-gray-600">
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div className="flex items-end">
                <img
                  src={selectedChat.sender.image}
                  alt={selectedChat.sender.name}
                  className="h-8 w-8 rounded-full object-cover mr-4"
                />
                <div className="bg-gray-100 rounded-2xl rounded-bl-none px-4 py-2 max-w-lg">
                  <p className="text-sm text-gray-900">{selectedChat.content}</p>
                  <span className="text-xs text-gray-500 mt-1">
                    {format(selectedChat.timestamp, 'h:mm a')}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center space-x-4">
                <input
                  type="text"
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-airbnb-red focus:border-transparent"
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                />
                <button
                  onClick={handleSendMessage}
                  className="bg-airbnb-red hover:bg-red-600 text-white p-2 rounded-lg transition-colors duration-200"
                >
                  <PaperAirplaneIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No message selected</h3>
              <p className="mt-1 text-sm text-gray-500">Select a conversation to start messaging</p>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default MessagesPage;